/**
 * The parts of a score, as rows that can be renamed, re-voiced, reordered and removed.
 *
 * A row's instrument picker changes what the part is played on and nothing else: the notes
 * already written stay on the staff they are on, which for a transposing instrument means
 * they now sound somewhere else. That is the editor's rule, stated in
 * `setup.changeInstrument`, and this board does not soften it.
 *
 * The key picker is the exception a score in a flat key needs. A part prints the piece's
 * key moved by its own transposition unless it is told otherwise, and the two ways it can
 * be told -- no signature at all, or the other spelling of the same one -- are here.
 */

import { INSTRUMENTS, setup } from "@app/score-editor";
import type { Command, InstrumentPreset, KeyPrinting, Part, Score } from "@app/score-editor";
import InstrumentPicker from "./InstrumentPicker";
import type { Pick } from "./InstrumentPicker";
import { Choice, Field, Text, Tiny } from "./ScoreSetupFields";
import type { Option } from "./ScoreSetupFields";

/** Every instrument, under the heading of its family. */
const CHOICES: readonly Pick[] = INSTRUMENTS.map((preset) => ({
  value: preset.id,
  label: preset.name,
  group: preset.family,
}));

/** The four ways a part can arrive at the signature it prints. */
const PRINTING: readonly Option<KeyPrinting>[] = [
  { value: "derived", label: "From the key" },
  { value: "none", label: "None" },
  { value: "sharps", label: "Sharps" },
  { value: "flats", label: "Flats" },
];

/** What a part is played on, or nothing when it came from a file naming something else. */
function presetOf(part: Part): InstrumentPreset | null {
  return INSTRUMENTS.find((preset) => preset.sound === part.instrument.sound) ?? null;
}

type Apply = (built: Command | string, label?: string) => void;

export interface ScorePartsProps {
  readonly score: Score;
  apply: Apply;
}

/**
 * One part.
 *
 * A part whose instrument is not one this picker offers keeps its own name at the head of
 * the list, so the box says what the part actually is rather than showing the first
 * instrument in the list as though it had been chosen.
 */
function PartRow({
  score,
  part,
  index,
  apply,
}: {
  score: Score;
  part: Part;
  index: number;
  apply: Apply;
}): JSX.Element {
  const preset = presetOf(part);
  const options =
    preset === null
      ? [{ value: "", label: `${part.instrument.name} (kept)` }, ...CHOICES]
      : CHOICES;

  const choose = (id: string): void => {
    const chosen = INSTRUMENTS.find((candidate) => candidate.id === id);
    if (chosen === undefined) return;
    apply(setup.changeInstrument(score, part.id, chosen), "change an instrument");
  };

  return (
    <div className="flex items-end gap-2">
      <span className="w-5 shrink-0 pb-3 text-right font-mono text-[11px] text-ink-quiet">
        {index + 1}
      </span>
      <Text
        label="Name"
        value={part.name}
        className="min-w-[120px] flex-1"
        onCommit={(name) => apply(setup.renamePart(score, part.id, name), "rename a part")}
      />
      <Field label="Instrument" className="min-w-[150px] flex-1">
        <InstrumentPicker
          look="paper"
          label={`Instrument of ${part.name}`}
          placeholder="Instrument"
          value={preset?.id ?? ""}
          options={options}
          onPick={choose}
        />
      </Field>
      <Choice
        label="Key sig."
        value={part.instrument.keyPrinting}
        options={PRINTING}
        className="min-w-[116px]"
        onChange={(printing) =>
          apply(setup.setKeyPrinting(score, part.id, printing), "change a key signature")
        }
      />
      <Tiny
        title={`Move ${part.name} up`}
        disabled={index === 0}
        onClick={() => apply(setup.movePart(score, part.id, index - 1), "move a part")}
      >
        ↑
      </Tiny>
      <Tiny
        title={`Move ${part.name} down`}
        disabled={index === score.parts.length - 1}
        onClick={() => apply(setup.movePart(score, part.id, index + 1), "move a part")}
      >
        ↓
      </Tiny>
      <Tiny
        title={`Remove ${part.name}`}
        onClick={() => apply(setup.removePart(score, part.id), "remove a part")}
      >
        ×
      </Tiny>
    </div>
  );
}

export default function ScoreParts({ score, apply }: ScorePartsProps): JSX.Element {
  const add = (id: string): void => {
    const preset = INSTRUMENTS.find((candidate) => candidate.id === id);
    if (preset === undefined) return;
    apply(setup.addPart(score, preset), `add ${preset.name}`);
  };

  return (
    <div className="space-y-2">
      {score.parts.map((part, index) => (
        <PartRow key={part.id} score={score} part={part} index={index} apply={apply} />
      ))}

      <Field label="Add a part" className="max-w-[260px]">
        <InstrumentPicker
          look="paper"
          label="Add a part"
          placeholder="Choose an instrument…"
          value={null}
          options={CHOICES}
          onPick={add}
        />
      </Field>
    </div>
  );
}
